import React from 'react';
import type { Checkin } from '../../types';
import { LoadingSpinner } from '../shared';
import styles from './CheckinHistory.module.css';

interface CheckinHistoryProps {
  checkins: Checkin[];
  isLoading: boolean;
  error: string | null;
}

export const CheckinHistory: React.FC<CheckinHistoryProps> = ({
  checkins,
  isLoading,
  error,
}) => {
  if (isLoading) {
    return (
      <div className={styles.loading}>
        <LoadingSpinner size="medium" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <p>{error}</p>
      </div>
    );
  }

  if (checkins.length === 0) {
    return (
      <div className={styles.empty}>
        <p>No check-ins yet</p>
      </div>
    );
  }

  const getScoreColor = (score: number): string => {
    if (score <= 2) return styles.low;
    if (score >= 4) return styles.high;
    return styles.medium;
  };

  return (
    <div className={styles.container}>
      <h3 className={styles.title}>Check-in History</h3>
      
      <div className={styles.list}>
        {checkins.map((checkin) => (
          <div key={checkin.id} className={styles.checkin}>
            <div className={styles.header}>
              <span className={styles.week}>Week {checkin.weekNumber}</span>
              <span className={styles.date}>
                {new Date(checkin.createdAt).toLocaleDateString()}
              </span>
            </div>

            <div className={styles.scores}>
              <div className={styles.score}>
                <span className={styles.scoreLabel}>Energy</span>
                <span className={`${styles.scoreValue} ${getScoreColor(checkin.energyScore)}`}>
                  {checkin.energyScore}
                </span>
              </div>
              <div className={styles.score}>
                <span className={styles.scoreLabel}>Difficulty</span>
                <span className={`${styles.scoreValue} ${getScoreColor(checkin.difficultyScore)}`}>
                  {checkin.difficultyScore}
                </span>
              </div>
              <div className={styles.score}>
                <span className={styles.scoreLabel}>Soreness</span>
                <span className={`${styles.scoreValue} ${getScoreColor(checkin.sorenessScore)}`}>
                  {checkin.sorenessScore}
                </span>
              </div>
            </div>

            {checkin.comment && (
              <div className={styles.comment}>
                <span className={styles.commentLabel}>Note:</span>
                <p className={styles.commentText}>{checkin.comment}</p>
              </div>
            )}

            {checkin.aiAdjustment && (
              <div className={styles.aiAdjustment}>
                <span className={styles.aiLabel}>🤖 AI Recommendation:</span>
                <p className={styles.aiText}>{checkin.aiAdjustment}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
